import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useTranslation } from 'react-i18next';
import type { BrandStrategy, GeoStrategy } from '@traaker/shared';
import { api } from '../../services/api';

/**
 * Self-contained two-layer strategy-suggestion dialog. Given a recommendation
 * (used as the `query`/`response` seed), it calls POST /brands/:id/strategy/suggest
 * and renders the Brand-Knowledge + GEO-Strategy diffs with per-layer Apply.
 *
 * This is the shared entry point for a `geo_strategy`-targeted recommendation
 * (the 3rd target icon). It reuses the SAME backend endpoint the BrandPerception
 * "Apparaître davantage" flow uses, so no new API surface is needed.
 */

const BRAND_ARRAY_FIELDS: (keyof BrandStrategy)[] = ['keywords', 'scenarios', 'differentiators', 'aliases'];
const BRAND_SCALAR_FIELDS: (keyof BrandStrategy)[] = ['industry', 'description', 'clientPersonas', 'businessModel', 'geographicFocus'];
const BRAND_LABEL_KEYS: Partial<Record<keyof BrandStrategy, string>> = {
  keywords: 'strategySuggest.field.keywords',
  scenarios: 'strategySuggest.field.scenarios',
  differentiators: 'strategySuggest.field.differentiators',
  aliases: 'strategySuggest.field.aliases',
  industry: 'strategySuggest.field.industry',
  description: 'strategySuggest.field.description',
  clientPersonas: 'strategySuggest.field.clientPersonas',
  businessModel: 'strategySuggest.field.businessModel',
  geographicFocus: 'strategySuggest.field.geographicFocus',
};

const GEO_ARRAY_FIELDS: (keyof GeoStrategy)[] = ['targetQueries', 'targetAuthoritySources', 'contentPillars', 'competitiveFocus'];
const GEO_SCALAR_FIELDS: (keyof GeoStrategy)[] = ['strategicAngle', 'queryStrategy'];
const GEO_LABEL_KEYS: Partial<Record<keyof GeoStrategy, string>> = {
  targetQueries: 'strategySuggest.field.targetQueries',
  targetAuthoritySources: 'strategySuggest.field.targetAuthoritySources',
  contentPillars: 'strategySuggest.field.contentPillars',
  competitiveFocus: 'strategySuggest.field.competitiveFocus',
  strategicAngle: 'strategySuggest.field.strategicAngle',
  queryStrategy: 'strategySuggest.field.queryStrategy',
};

function diffArrays(a: string[] = [], b: string[] = []) {
  const aSet = new Set(a);
  const bSet = new Set(b);
  return { added: b.filter((x) => !aSet.has(x)), removed: a.filter((x) => !bSet.has(x)) };
}

interface SuggestResponse {
  success: boolean;
  explanation: string;
  currentStrategy: BrandStrategy;
  suggestedStrategy: BrandStrategy;
  currentGeoStrategy: GeoStrategy;
  suggestedGeoStrategy: GeoStrategy;
  hasBrandChange: boolean;
  hasGeoChange: boolean;
}

function ArrayDiff({
  fields,
  labels,
  current,
  suggested,
}: {
  fields: string[];
  labels: Record<string, string>;
  current: Record<string, unknown>;
  suggested: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <>
      {fields.map((field) => {
        const { added, removed } = diffArrays(
          (current[field] as string[]) || [],
          (suggested[field] as string[]) || [],
        );
        if (!added.length && !removed.length) return null;
        return (
          <Box key={field}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {labels[field] ? t(labels[field]) : field}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {added.map((v) => (
                <Chip key={`a-${v}`} label={`+ ${v}`} size="small" sx={{ bgcolor: '#00DDB320', color: '#00DDB3', fontWeight: 600 }} />
              ))}
              {removed.map((v) => (
                <Chip key={`r-${v}`} label={`− ${v}`} size="small" sx={{ bgcolor: '#FF444420', color: '#FF4444', fontWeight: 600, textDecoration: 'line-through' }} />
              ))}
            </Box>
          </Box>
        );
      })}
    </>
  );
}

function ScalarDiff({
  fields,
  labels,
  current,
  suggested,
}: {
  fields: string[];
  labels: Record<string, string>;
  current: Record<string, unknown>;
  suggested: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <>
      {fields.map((field) => {
        const before = (current[field] as string) || '';
        const after = (suggested[field] as string) || '';
        if (before === after || !after) return null;
        return (
          <Box key={field}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {labels[field] ? t(labels[field]) : field}
            </Typography>
            <Box sx={{ bgcolor: 'rgba(11,11,15,0.03)', borderRadius: 0.5, p: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#00A88A' }}>
                {after}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </>
  );
}

export function StrategySuggestDialog({
  open,
  onClose,
  brandId,
  auditId,
  seedQuery,
  seedResponse,
  axis,
  onApplied,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  auditId?: string;
  /** Recommendation title — used as the `query` seed for the suggest endpoint. */
  seedQuery: string;
  /** Recommendation description — used as the `response` seed. */
  seedResponse: string;
  /** narrative | authority | referral | website | earned_media (best-effort). */
  axis: string;
  onApplied?: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState<SuggestResponse | null>(null);
  const [error, setError] = useState('');

  // Fetch the suggestion when the dialog opens (once per open).
  const [fetchedFor, setFetchedFor] = useState('');
  const openKey = open ? `${brandId}:${seedQuery}` : '';
  if (open && openKey !== fetchedFor && !loading) {
    setFetchedFor(openKey);
    setData(null);
    setError('');
    setLoading(true);
    api
      .post<SuggestResponse>(`/brands/${brandId}/strategy/suggest`, {
        query: seedQuery,
        response: seedResponse || seedQuery,
        axis,
        auditId,
      })
      .then((res) => setData(res))
      .catch(() => setError(t('strategySuggest.error.generate')))
      .finally(() => setLoading(false));
  }

  const hasChanges = useMemo(() => !!(data && (data.hasBrandChange || data.hasGeoChange)), [data]);

  const handleApply = async () => {
    if (!data) return;
    setApplying(true);
    try {
      const calls: Promise<unknown>[] = [];
      if (data.hasBrandChange) calls.push(api.patch(`/brands/${brandId}/strategy`, data.suggestedStrategy));
      if (data.hasGeoChange) calls.push(api.patch(`/brands/${brandId}/geo-strategy`, data.suggestedGeoStrategy));
      await Promise.all(calls);
      onApplied?.();
      onClose();
    } catch {
      setError(t('strategySuggest.error.apply'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: '#7C3AED' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('strategySuggest.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('strategySuggest.suggestionFrom', { query: seedQuery.slice(0, 90) })}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && !loading && <Alert severity="error">{error}</Alert>}
        {!loading && !error && data && (
          <>
            {data.explanation && hasChanges && (
              <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 2 }}>
                {data.explanation}
              </Alert>
            )}
            {!hasChanges && (
              <Alert severity="warning">
                {t('strategySuggest.noChanges')}
              </Alert>
            )}
            {hasChanges && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {data.hasBrandChange && (
                  <Box>
                    <Chip label="Brand Knowledge" size="small" sx={{ mb: 1, bgcolor: '#7C3AED18', color: '#7C3AED', fontWeight: 700 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <ArrayDiff fields={BRAND_ARRAY_FIELDS as string[]} labels={BRAND_LABEL_KEYS as Record<string, string>} current={data.currentStrategy as Record<string, unknown>} suggested={data.suggestedStrategy as Record<string, unknown>} />
                      <ScalarDiff fields={BRAND_SCALAR_FIELDS as string[]} labels={BRAND_LABEL_KEYS as Record<string, string>} current={data.currentStrategy as Record<string, unknown>} suggested={data.suggestedStrategy as Record<string, unknown>} />
                    </Box>
                  </Box>
                )}
                {data.hasGeoChange && (
                  <Box>
                    <Chip label="GEO Strategy" size="small" sx={{ mb: 1, bgcolor: '#0EA5E918', color: '#0284C7', fontWeight: 700 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <ArrayDiff fields={GEO_ARRAY_FIELDS as string[]} labels={GEO_LABEL_KEYS as Record<string, string>} current={data.currentGeoStrategy as Record<string, unknown>} suggested={data.suggestedGeoStrategy as Record<string, unknown>} />
                      <ScalarDiff fields={GEO_SCALAR_FIELDS as string[]} labels={GEO_LABEL_KEYS as Record<string, string>} current={data.currentGeoStrategy as Record<string, unknown>} suggested={data.suggestedGeoStrategy as Record<string, unknown>} />
                    </Box>
                  </Box>
                )}
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('strategySuggest.draftWarning')}
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          {t('strategySuggest.close')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applying || loading || !hasChanges}
          startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {applying ? t('strategySuggest.applying') : t('strategySuggest.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
